import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { MainLayout } from '@templates/index';
import { Button, Card } from '@atoms/index';
import { formatCurrency } from '@utils/currency';
import { storage, STORAGE_KEYS } from '@utils/storage';
import type { Order } from '@/types';

const OrderConfirmationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState<Order | null>(
    (location.state as { order?: Order } | null)?.order ?? null,
  );
  const [trackingUrl, setTrackingUrl] = useState<string | null>(
    (location.state as { trackingUrl?: string } | null)?.trackingUrl ?? null,
  );

  useEffect(() => {
    if (order) {
      storage.set(STORAGE_KEYS.GUEST_ORDER_CONFIRMATION, order);
      if (trackingUrl) {
        storage.set(STORAGE_KEYS.GUEST_ORDER_TRACKING_URL, trackingUrl);
      }
      return;
    }

    const cached = storage.get<Order>(STORAGE_KEYS.GUEST_ORDER_CONFIRMATION);
    if (cached) {
      setOrder(cached);
      const cachedTrackingUrl = storage.get<string>(STORAGE_KEYS.GUEST_ORDER_TRACKING_URL);
      if (cachedTrackingUrl) {
        setTrackingUrl(cachedTrackingUrl);
      }
      return;
    }

    const orderNumber = searchParams.get('orderNumber');
    if (!orderNumber) {
      navigate('/');
    }
  }, [order, navigate, searchParams, trackingUrl]);

  const handleTrackOrder = () => {
    if (trackingUrl) {
      window.location.assign(trackingUrl);
      return;
    }
    navigate('/siparis-takip');
  };

  if (!order) {
    const orderNumber = searchParams.get('orderNumber');
    return (
      <MainLayout>
        <div className="max-w-lg mx-auto text-center py-16">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Siparişiniz Alındı</h1>
          {orderNumber && (
            <p className="text-gray-600 mb-6">
              Sipariş numaranız: <span className="font-semibold">{orderNumber}</span>
            </p>
          )}
          <p className="text-sm text-gray-500 mb-8">
            Sipariş takip bağlantısı e-posta adresinize gönderildi. Bağlantı gelmediyse aşağıdan tekrar isteyebilirsiniz.
          </p>
          <div className="flex flex-col gap-3">
            <Button onClick={handleTrackOrder}>Siparişi Takip Et</Button>
            <Button variant="outline" onClick={() => navigate('/')}>Alışverişe Devam Et</Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-lg mx-auto py-12">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Siparişiniz Alındı!</h1>
          <p className="text-gray-600">
            Sipariş numaranız:{' '}
            <span className="font-bold text-primary">{order.orderNumber}</span>
          </p>
        </div>

        <Card className="space-y-4 mb-8">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Teslimat Adresi</p>
            <p className="text-gray-900 font-medium">{order.deliveryAddress.fullAddress}</p>
          </div>
          {(order as Order & { restaurant?: { name?: string } }).restaurant?.name && (
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Restoran</p>
              <p className="text-gray-900 font-medium">
                {(order as Order & { restaurant?: { name?: string } }).restaurant?.name}
              </p>
            </div>
          )}
          <div className="flex justify-between pt-2 border-t border-gray-100">
            <span className="text-gray-600">Toplam</span>
            <span className="font-bold text-gray-900">{formatCurrency(order.total)}</span>
          </div>
        </Card>

        <p className="text-sm text-gray-500 text-center mb-6">
          Sipariş takip bağlantısı e-posta adresinize gönderildi. Aşağıdaki butonla hemen takip edebilirsiniz.
        </p>

        <div className="flex flex-col gap-3">
          <Button onClick={handleTrackOrder}>Siparişi Takip Et</Button>
          <Button variant="outline" onClick={() => navigate('/')}>Alışverişe Devam Et</Button>
          <Link to="/siparis-takip" className="text-center text-sm text-gray-500 hover:text-primary">
            Takip bağlantısı gelmedi mi?
          </Link>
        </div>
      </div>
    </MainLayout>
  );
};

export default OrderConfirmationPage;
